import { axiosInstance } from '../utils/axios';
import { MockTestData } from '../models/MockTestData';
import { SeriesConfig, testSeriesConfig } from '../data/seriesConfig';

export interface TestSessionRequest {
  grade: string;
  series: string;
  studentId: string;
  subject: string;
  studentName?: string; // Student's name
  studentPhone?: string; // Student's phone number
  openingDate?: string; // Optional as we'll get it from seriesConfig
  closingDate?: string; // Optional as we'll get it from seriesConfig
}

export interface TestSessionResponse {
  id: string;
  studentId: string;
  packageId: string;
  packageName: string;
  grade: string[];
  subject: string[];
  series: string[];
  timeLeft: number;
  status: string;
  startTime: string;
  endTime: string | null;
  score: number;
  totalScore: number;
  currentQuestionIndex: number;
  totalDuration: number;
  totalNoOfQuestions: number;
  openingDate: string;
  closingDate: string;
}

export const fetchTestSession = async (request: TestSessionRequest): Promise<MockTestData> => {
  try {
    // Find the series config to get opening and closing dates
    const seriesConfig = testSeriesConfig.find(series => series.seriesName === request.series);
    
    // Create the request with dates from config
    const requestWithDates = {
      ...request,
      openingDate: seriesConfig?.openingDate,
      closingDate: seriesConfig?.closingDate
    };
    
    // Make the API call with the enhanced request
    const response = await axiosInstance.post<{ status: string; message: string; data: TestSessionDTO }>('/sessions', requestWithDates);
    // Extract session data from the response wrapper
    const data = response.data.data;
    
    // If there was an error or data is null, throw an error
    if (response.data.status === 'error' || !data) {
      throw new Error(response.data.message || 'Failed to create session');
    }
    
    // Format title from packageName (e.g. "Class-04 - Science - Mock Test - 01" → "Class-04 Science Mock Test - 01")
    let formattedTitle = data?.packageName || `${request.grade} ${request.subject} ${request.series}`;
    
    // Only attempt to format if we have a packageName
    if (formattedTitle) {
      formattedTitle = formattedTitle.replace(/^(Class-[0-9]+)\s*-\s*([^\s-]+)\s*-\s*/, '$1 $2 ');
    }
    
    // Map API status to our frontend status format
    let testStatus: 'open' | 'upcoming' | 'closed' = 'open';
    if (data?.status) {
      switch (data.status) {
        case 'IN_PROGRESS':
          testStatus = 'open';
          break;
        case 'COMPLETED':
          testStatus = 'closed';
          break;
        case 'UPCOMING':
          testStatus = 'upcoming';
          break;
        default:
          testStatus = 'open';
      }
    }
    
    // Calculate progress percentage - handle division by zero
    const totalQuestions = data?.totalNoOfQuestions || 0;
    // Calculate completed questions from answers array, not currentQuestionIndex
    const completedQuestions = data?.answers ? data.answers.length : 0;
    const progress = totalQuestions > 0 ? (completedQuestions / totalQuestions) * 100 : 0;
    
    // Create the mock test data object with values from the response
    return {
      sessionId: data?.id || `${request.series}-${request.subject}-${Math.random().toString(36).substring(2, 10)}`,  // Changed from testId
      title: formattedTitle,
      subject: request.subject,
      progress: progress,
      color: getSubjectColor(request.subject),
      totalQuestions: data?.totalNoOfQuestions || 0,
      completedQuestions: completedQuestions,
      status: testStatus,
    };
  } catch (error) {
    
    // Return a fallback test data object instead of throwing
    return {
      sessionId: `fallback-${request.series}-${request.subject}-${Math.random().toString(36).substring(2, 10)}`,  // Changed from testId
      title: `${request.grade} ${request.subject} ${request.series}`,
      subject: request.subject,
      progress: 0,
      color: getSubjectColor(request.subject),
      totalQuestions: 0,
      completedQuestions: 0,
      status: 'upcoming' as 'open' | 'upcoming' | 'closed',
    };
  }
};

// Helper function to get subject color based on subject name
const getSubjectColor = (subject: string): string => {
  switch (subject) {
    case 'Science':
      return '#06B6D4';
    case 'Math':
      return '#2563EB';
    case 'English':
      return '#FF8C00';
    default:
      return '#6366F1';
  }
};

// Interface for the batch create test series request
export interface BatchCreateTestSeriesRequest {
  studentId: string;
  grade: string;
  studentName?: string; // Student's name
  studentPhone?: string; // Student's phone number
  series: SeriesConfig[];
  autoAddQuestions: boolean;
}

// Individual session DTO returned from backend
export interface TestSessionDTO {
  id: string;
  studentId: string;
  packageId: string;
  packageName: string;
  grade: string;
  subject: string;
  series: string;
  totalNoOfQuestions: number;
  totalDuration: number;
  timeLeft: number;
  lastActivityTime: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  openingDate: string;
  closingDate: string;
  status: string; // "ACTIVE", "COMPLETED", etc.
  startTime: string;
  endTime: string | null;
  score: number;
  totalScore: number;
  answers: any[];
  currentQuestionIndex: number;
}

// Response from batch API
export interface BatchCreateTestSeriesResponse {
  status: string;
  message: string;
  data: {
    studentId: string;
    sessions: Record<string, TestSessionDTO[]>;
  };
}

/**
 * Creates a batch of test sessions for all subjects and series in a single API call
 * @param request The batch create request with student ID, grade, and series config
 * @returns A 2D array of MockTestData objects, organized by subject
 */
// Helper to convert simple date strings to ISO format compatible with Java's Instant
const toInstantCompatibleFormat = (dateStr: string): string => {
  // If the date is already in ISO format with time, return it
  if (dateStr.includes('T')) return dateStr;
  
  // Otherwise append time component to make it a full ISO string (noon UTC)
  return `${dateStr}T12:00:00Z`;
};

export const batchCreateTestSeries = async (request: BatchCreateTestSeriesRequest) => {
  try {
    // Create a copy of the request and convert all dates to Instant-compatible format
    const requestWithFormattedDates = {
      ...request,
      series: request.series.map(series => {
        const formattedSeries = {
          ...series,
          openingDate: toInstantCompatibleFormat(series.openingDate),
          closingDate: toInstantCompatibleFormat(series.closingDate)
        };
        return formattedSeries;
      })
    };
    
    const response = await axiosInstance.post<BatchCreateTestSeriesResponse>('/sessions/series', requestWithFormattedDates);
    
    // If there was an error or data is null, throw an error
    if (response.data.status === 'error' || !response.data.data) {
      throw new Error(response.data.message || 'Failed to create test sessions');
    }
    
    // Return the original response data structure for maximum compatibility
    // The Redux slice will handle the transformation
    return response.data.data;
  } catch (error) {
    // Return an empty record in case of error
    return {};
  }
};

// ========== New API Functions for Test Taking ==========

/**
 * Fetches a test session by ID
 * @param sessionId - The ID of the session to fetch
 * @returns The session details including packageId
 */

export const fetchSession = async (sessionId: string): Promise<SessionResponse | null> => {
  if (!sessionId) {
    return null;
  }
  
  try {
    // Make the API call with explicit error handling
    const response = await axiosInstance.get(`/sessions/${sessionId}`);
    
    // Log the response status
    
    // Validate response data
    if (!response.data) {
      return null;
    }
    
    // Process the session data
    const sessionData = response.data as SessionResponse;
    
    try {
      // Debug log critical session properties
      
      // Make sure timeRemaining is populated from timeLeft if available
      if (sessionData.timeLeft !== undefined) {
        sessionData.timeRemaining = sessionData.timeLeft;
      } else if (!sessionData.timeRemaining) {
        // Default time remaining to 30 minutes in seconds
        sessionData.timeRemaining = 1800;
      }
      
      // Generate activities from answers for state restoration
      if (sessionData.answers && sessionData.answers.length > 0) {
        
        // Create synthetic activities from answers
        const activities: SessionActivity[] = [];
        
        for (let i = 0; i < sessionData.answers.length; i++) {
          const answer = sessionData.answers[i];
          if (!answer || !answer.questionId) continue;
          
          activities.push({
            id: `activity-${answer.questionId}-${i}`,
            sessionId: sessionId,
            questionId: answer.questionId,
            questionIndex: i, 
            startTime: answer.timestamp,
            endTime: undefined,
            totalTimeTaken: 0,
            answerIndex: answer.answer !== null ? answer.answer : undefined,
            answerText: undefined,
            result: answer.correct,
            isMarkedForReview: false,
            status: answer.answer !== null ? 'ANSWERED' : 'VIEWED'
          });
        }
        
        // Add activities to session data
        sessionData.activities = activities;
      } else {
        // Initialize empty activities array
        sessionData.activities = [];
      }
    } catch (innerError) {
      // If we encounter errors processing the session data, log them but still return the basic session data
      // Ensure activities exists even in error case
      sessionData.activities = sessionData.activities || [];
    }
    
    // Always return the session data, even if we had problems processing it
    return sessionData;
    
  } catch (error) {
    return null;
  }
};

/**
 * Interface for session creation
 */
export interface CreateSessionRequest {
  studentId: string;
  packageId: string;
  studentName?: string; // Student's name
  studentPhone?: string; // Student's phone number
  autoAddQuestions?: boolean;
}

/**
 * Creates a test session for a specific package
 * @param request Session creation details
 * @returns The created session ID and details
 */
export const createTestSession = async (request: CreateSessionRequest): Promise<string> => {
  try {
    const response = await axiosInstance.post('/sessions', request);
    
    if (response.data.status === 'success' && response.data.data) {
      // Return the session ID
      return response.data.data.id || '';
    }
    return '';
  } catch (error: any) {
    if (error.response) {
    }
    return '';
  }
};

// Interface for a question from the backend API
export interface PackageQuestion {
  id: string;
  question: string; // API returns 'question', not 'text'
  options: string[];
  correctAnswer?: number | null; // API returns 'correctAnswer'
  explanation?: string;
  hints?: string[];
  score?: number; // API uses 'score' instead of 'points'
  duration?: number;
  createdAt?: string;
  updatedAt?: string;
  tags?: string[];
  mediaUrl?: string; // Audio file URL for listening questions
  // Add any other fields that might be needed
}

// Interface for the full package questions response
export interface PackageQuestionsResponse {
  status: string;
  message: string;
  data: {
    packageId: string;
    packageName: string;
    grade: string;
    subject: string;
    series: string;
    totalQuestions: number;
    questions: PackageQuestion[];
  }
}

/**
 * Fetches all questions for a specific package
 * @param packageId ID of the package
 */
export const fetchPackageQuestions = async (packageId: string): Promise<PackageQuestion[]> => {
  try {
    // Use the unified endpoint - backend will intelligently choose light vs heavy API based on hasMedia tag
    const url = `/packages/${packageId}/questions`;
    
    const response = await axiosInstance.get<PackageQuestionsResponse>(url);
    
    if (response.data.status === 'success' && response.data.data && response.data.data.questions) {
      return response.data.data.questions;
    }
    return [];
  } catch (error: any) { // Type assertion for error object
    // More detailed error logging
    if (error.response) {
      // Server responded with an error
    } else if (error.request) {
      // Request was made but no response received
    } else {
      // Error in setting up the request
    }
    return [];
  }
};

/**
 * Interface for tracking question loading in a session
 * The parameter name 'questionId' matches the backend API's expected parameter
 */
export interface LoadQuestionRequest {
  previousQuestionId: string | null;
  questionId: string; // Backend expects 'questionId', not 'currentQuestionId'
}

/**
 * Tracks when a question is loaded/viewed
 * This is a non-blocking call (fire and forget) since the UI doesn't need the response
 * @param sessionId ID of the current session
 * @param request Load question request with previous and current question IDs
 */
export const trackQuestionLoad = (sessionId: string, request: LoadQuestionRequest): void => {
  // Fire and forget - don't wait for response or use await
  axiosInstance.post(`/activities/${sessionId}/load-question`, request)
    .then(() => {
      // Optional: Log successful tracking for debugging
    })
    .catch(error => {
      // Just log errors but don't block the UI
    });
  
  // Return immediately without waiting
  return;
};

/**
 * Interface for submitting an answer to a question
 */
export interface SubmitAnswerRequest {
  questionId: string;
  answerIndex: number;
  answerText?: string; // For open-ended questions
}

/**
 * Submits an answer for a question
 * @param sessionId ID of the current session
 * @param request Answer submission details
 */
export const submitAnswer = async (sessionId: string, request: SubmitAnswerRequest) => {
  try {
    // Validate answer index before submission
    const originalAnswerIndex = request.answerIndex;
    
    // Ensure answerIndex is between 0-3 (options A-D)
    if (originalAnswerIndex < 0 || originalAnswerIndex > 3 || !Number.isInteger(originalAnswerIndex)) {
      // Fix the answer index if it seems like an ASCII value (A=65, B=66, etc.)
      if (originalAnswerIndex >= 65 && originalAnswerIndex <= 68) {
        request = {
          ...request,
          answerIndex: originalAnswerIndex - 65  // Convert A=65 to index 0
        };
      } else if (originalAnswerIndex === 64) { // ASCII '@' character - could be off by 1 error
        request = {
          ...request,
          answerIndex: 0  // Default to first option
        };
      } else {
        // Default to first option if completely invalid
        request = {
          ...request,
          answerIndex: 0
        };
      }
    }
    
    const response = await axiosInstance.post(`/activities/${sessionId}/answers`, request);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Interface for marking a question for review
 */
export interface MarkReviewRequest {
  questionId: string;
  isMarked: boolean;
}

/**
 * Marks a question for review
 * @param sessionId ID of the current session
 * @param request Mark for review request
 */
export const markQuestionForReview = async (sessionId: string, request: MarkReviewRequest) => {
  try {
    const response = await axiosInstance.post(`/activities/${sessionId}/mark-review`, request);
    return response.data;
  } catch (error) {
  }
};

/**
 * Interface for session activity
 */
export interface SessionActivity {
  id: string;
  sessionId: string;
  questionId: string;
  questionIndex?: number;
  startTime?: Date | string;
  endTime?: Date | string;
  totalTimeTaken?: number;
  answerIndex?: number;
  answerText?: string;
  result?: boolean;
  isMarkedForReview?: boolean;
  status?: string; // Allow any string value for status including 'VIEWED'
}

/**
 * Answer record from the session API response
 */
export interface AnswerRecord {
  questionId: string;
  answer: number | null;
  timestamp: string;
  correct?: boolean;
}

/**
 * Interfaces and types for session management
 */
export interface SessionResponse {
  id: string;
  mode?: string;
  categoryId?: string;
  packageId: string;
  studentId?: string;
  packageName?: string;
  grade?: string;
  subject?: string;
  series?: string;
  status: string;
  isMarked?: boolean;
  startedOn?: Date;
  endedOn?: Date | null;
  submitTime?: Date | null;
  timeRemaining?: number;
  timeLeft?: number;
  totalNoOfQuestions?: number;
  totalDuration?: number;
  answers?: AnswerRecord[];
  timeSpent?: number;
  currentQuestionIndex?: number;
  totalMarks?: number;
  score?: number;
  totalScore?: number;
  result?: string;
  metadata?: any;
  activities?: SessionActivity[];
  startTime?: string;
  endTime?: string;
  openingDate?: string;
  closingDate?: string;
}

/**
 * Fetch all activities for a session (for resuming a test)
 * @param sessionId ID of the current session
 * @returns List of session activities with question IDs and answers
 */
export const fetchSessionActivities = async (sessionId: string): Promise<SessionActivity[]> => {
  try {
    const response = await axiosInstance.get(`/activities/${sessionId}`);
    
    if (response.data.status === 'success' && Array.isArray(response.data.data)) {
      return response.data.data as SessionActivity[];
    }
    
    return [];
  } catch (error: any) {
    if (error.response) {
    }
    return [];
  }
};

/**
 * Ends a test session
 * @param sessionId ID of the session to end
 */
export const endTestSession = async (sessionId: string) => {
  try {
    const response = await axiosInstance.put(`/sessions/${sessionId}/end`);
    return response.data;
  } catch (error) {
  }
};

/**
 * Interface for session result data
 */
export interface SessionResult {
  id: string;
  studentId: string;
  score: number;
  totalScore: number;
  totalQuestions: number;
  answeredQuestions: number;
  scoreBySubject: Record<string, number> | null;
  questionResults: {
    questionId: string;
    questionIndex: number;
    answerIndex: number | null;
    answerText: string | null;
    isCorrect: boolean;
    timeTaken: number;
    isMarkedForReview: boolean;
  }[];
  startTime: string;
  endTime: string | null;
}

/**
 * Fetches test session results
 * @param sessionId ID of the session
 */
export const fetchSessionResults = async (sessionId: string): Promise<SessionResult | null> => {
  try {
    const response = await axiosInstance.get(`/sessions/${sessionId}/result`);
    
    if (response.data.status === 'success' && response.data.data) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    return null;
  }
};
